import { useCallback, useEffect, useRef } from 'react'

import { useAuth } from '@/middlewares/auth/auth-context'
import { userUpdate } from '@/middlewares/services/user.services'
import {
    isTourDismissed,
    isTourDone,
    readLocalTours,
    toursMissingOnServer,
    withTourResult,
    writeLocalTours,
} from './tourProgress.storage'

/**
 * Reads and writes a tour's outcome on the account (`user.systemPreferences.tours`) and in the local
 * mirror. `markDone` writes the mirror first (instant, offline-proof), updates the auth context, then
 * PUTs the minimal body `userUpdate` accepts (`email` is required by the service, `systemPreferences`
 * is whitelisted and replaced whole — hence the merge). A failed PUT is logged, never blocking: the
 * mirror keeps the tour from coming back, and the next load syncs it up once.
 */
export default function useTourProgress() {
    const auth = useAuth()
    const user = auth.user
    const serverTours = user?.systemPreferences?.tours
    const syncedFor = useRef(null)

    const save = useCallback(
        async (tours) => {
            const current = auth.user
            if (!current?._id) return
            const systemPreferences = { ...(current.systemPreferences || {}), tours }
            auth.updateUser({ systemPreferences })
            try {
                await userUpdate(current._id, { user: { email: current.email, systemPreferences } })
            } catch (error) {
                console.error('tour progress save failed', error)
            }
        },
        [auth],
    )

    const isDone = useCallback((def) => isTourDone(def, serverTours, readLocalTours()), [serverTours])
    const isDismissed = useCallback((def) => isTourDismissed(def, serverTours, readLocalTours()), [serverTours])

    const markDone = useCallback(
        (def, outcome = {}) => {
            writeLocalTours(withTourResult(readLocalTours(), def, outcome))
            return save(withTourResult(serverTours, def, outcome))
        },
        [save, serverTours],
    )

    // one-shot upward sync: outcomes recorded locally while a PUT failed (or offline) reach the account
    useEffect(() => {
        const id = user?._id
        if (!id || syncedFor.current === id) return
        syncedFor.current = id
        const missing = toursMissingOnServer(readLocalTours(), serverTours)
        if (missing) save({ ...(serverTours || {}), ...missing })
    }, [user?._id, serverTours, save])

    return { isDone, isDismissed, markDone }
}
